import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormType, LocationsType, SelectOption } from '../../../shared/models/form';
import AsyncSelect from 'react-select/async';
import fetchAirports from './fetchAirports';
import { DestinationTypes } from '../../../shared/constants';
import CustomOption from './CustomOption';
import CustomMenuList from './CustomMenuList';
import { useEffect, useState } from 'react';

interface LocationProps {
  label: string;
  placeholder: string;
  type: LocationsType;
  fieldIndex: number;
}

const Location = ({ label, placeholder, type, fieldIndex }: LocationProps) => {
  const { control, getValues } = useFormContext<FormType>();
  const fieldName = `bounds.${fieldIndex}.${type}` as const;
  const { t } = useTranslation();

  const [defaultOptions, setDefaultOptions] = useState<SelectOption[] | undefined>();

  const {
    field: { onChange, onBlur, value, ref, name },
    fieldState: { error },
  } = useController({ control, name: fieldName, rules: { required: true } });

  const origin = useWatch({
    control,
    name: `bounds.${fieldIndex}.origin`,
    disabled: type === 'origin',
  });

  useEffect(() => {
    onChange(null);
    setDefaultOptions(undefined);
  }, [onChange, origin?.value]);

  const { CITY, FNF, FNR } = DestinationTypes;

  const focusHandler = async () => {
    if (type === 'destination' && !!origin?.value && [FNF, FNR].includes(origin?.type)) {
      const values = await fetchAirports(fieldIndex, '  ', getValues);
      setDefaultOptions(values);
    }
  };

  return (
    <div>
      <label className="text-sm mb-2 block px-2">{label}</label>
      <AsyncSelect
        isClearable
        placeholder={placeholder}
        id={fieldName}
        key={fieldName}
        loadingMessage={() => t('Loading')}
        loadOptions={(inputValue) => fetchAirports(fieldIndex, inputValue, getValues)}
        onFocus={focusHandler}
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        ref={ref}
        name={name}
        defaultOptions={defaultOptions}
        isSearchable={type === 'origin' || (type === 'destination' && !!origin?.value)}
        components={{
          DropdownIndicator: null,
          IndicatorSeparator: null,
          Option: CustomOption,
          MenuList: CustomMenuList,
        }}
        noOptionsMessage={({ inputValue }) => {
          if (type === 'destination' && !origin?.value) {
            return t('No destinations available');
          }
          return inputValue.length < 2
            ? t('Type at least two characters to search')
            : t('No results found');
        }}
        unstyled
        className="static"
        classNames={{
          control: (state) => {
            return `w-full h-12 bg-white border ring-gray-500 focus:ring-2 rounded ${
              error && !state.menuIsOpen && !state.isFocused
                ? 'border-red-500'
                : 'border-input-gray'
            } px-4 ${state.menuIsOpen ? 'rounded-b-none' : ''} ${
              state.isFocused ? 'ring-2 ring-gray-500 border-input-gray' : ''
            }`;
          },
          menuList: () => {
            return `bg-white border-input-gray mt-0 rounded-b ring-2 ring-gray-500 border-t-none border-transparent`;
          },
          option: (state) =>
            `${state.isFocused ? 'bg-nok-green' : ''} ${
              [CITY, FNF, FNR].includes(state.data.location.type) ? 'ps-12' : 'ps-16'
            } pe-4 py-3.5 border`,
          noOptionsMessage: () => 'p-4 text-slate-700',
          loadingMessage: () => 'p-4',
          placeholder: () => 'text-nok-gray',
        }}
      />
    </div>
  );
};

export default Location;
